import { FC, MutableRefObject, ReactNode } from "react";
import { DialogTitle, Divider, IconButton } from "@mui/material";
import {
    OutsideModalIconButton,
    StyledDialog,
    StyledDialogActions,
    StyledDialogContent,
} from "./ModalConstructor.styles";
import { isRunningInNativeApp } from "../../../Infrastructure/Helpers/IsRunningInNativeApp";
import { CloseIcon } from "../Icon/CloseIcon";
import { useBodyScrollModalControlState } from "./Hooks/useBodyScrollModalControl";
import { isMobileAppDeviceOutput } from "../../../Domain/DeviceOutputState";
import { ModalPortal } from "../../../Infrastructure/Persistence/CustomPortal";
import { useInputFocusedStore } from "./CommentFormModal/useInputFocusedStore";

interface ModalConstructorProps {
    isBorderRadiusVisible?: boolean;
    isInsideClosingButtonVisible?: boolean;
    isOutsideClosingButtonVisible?: boolean;
    showModalActionsDivider?: boolean;
    isPaddingVisible?: boolean;
    modalActions?: ReactNode;
    modalContent?: ReactNode;
    modalInnerContainerStyles?: object;
    modalTitle?: ReactNode;
    onClose?: () => void;
    onCloseTransitionExited?: (node: HTMLElement) => void;
    open?: boolean;
    scrollableContainerRef?: MutableRefObject<HTMLDivElement | undefined>;
}

export const ModalConstructor: FC<ModalConstructorProps> = (props) => {
    const {
        onClose,
        open,
        modalTitle,
        modalContent,
        modalActions,
        isPaddingVisible = false,
        isOutsideClosingButtonVisible = false,
        isInsideClosingButtonVisible = false,
        isBorderRadiusVisible = true,
        showModalActionsDivider = true,
        modalInnerContainerStyles,
        onCloseTransitionExited,
        scrollableContainerRef,
    } = props;

    const showPadding = isRunningInNativeApp();

    useBodyScrollModalControlState(open as boolean);
    const isMobileApp = isMobileAppDeviceOutput();
    const { inputFocused } = useInputFocusedStore();

    return (
        <ModalPortal>
            <StyledDialog
                TransitionProps={{
                    onExited: (node) => {
                        onCloseTransitionExited?.(node);
                    },
                }}
                inputFocused={inputFocused}
                disablePortal={true}
                // fullWidth={true}
                isBorderRadiusVisible={isBorderRadiusVisible}
                onClose={() => onClose?.()}
                open={!!open}
                isMobileApp={isMobileApp}
                modalInnerContainerStyles={modalInnerContainerStyles}
                sx={{
                    "& .MuiDialog-container": {
                        "@media (max-width: 639px)": {
                            alignItems: "flex-end",
                            paddingBottom: isMobileApp
                                ? "8px"
                                : "calc(var(--safe-area-inset-bottom) + 8px)",
                            paddingTop:
                                "calc(var(--safe-area-inset-top) + 8px)",
                            height: isMobileApp
                                ? "calc(100vh - var(--safe-area-inset-top) - var(--safe-area-inset-bottom))"
                                : "calc(100% - var(--safe-area-inset-top) - var(--safe-area-inset-bottom))",
                        },
                    },
                    paddingTop: showPadding ? "var(--safe-area-inset-top)" : 0,
                }}
            >
                {isOutsideClosingButtonVisible ? (
                    <OutsideModalIconButton
                        aria-label="close"
                        onClick={() => onClose?.()}
                    >
                        <CloseIcon />
                    </OutsideModalIconButton>
                ) : null}
                {(modalTitle || isInsideClosingButtonVisible) && (
                    <DialogTitle
                        sx={{
                            m: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: modalTitle
                                ? "space-between"
                                : "end",
                            marginBottom: "16px",
                            padding: "16px 16px 0px 16px",
                            fontWeight: "700",
                            fontSize: "20px",
                        }}
                        component={"div"}
                    >
                        {modalTitle && modalTitle}
                        {isInsideClosingButtonVisible ? (
                            <IconButton
                                aria-label="close"
                                onClick={() => onClose?.()}
                                sx={{
                                    color: (theme) => theme.palette.grey[500],
                                }}
                            >
                                <CloseIcon />
                            </IconButton>
                        ) : null}
                    </DialogTitle>
                )}

                <StyledDialogContent
                    isPaddingVisible={isPaddingVisible}
                    isBorderRadiusVisible={isBorderRadiusVisible}
                    dividers={false}
                    ref={scrollableContainerRef}
                >
                    {modalContent}
                </StyledDialogContent>
                {modalActions && (
                    <>
                        {showModalActionsDivider && <Divider />}
                        <StyledDialogActions>
                            {modalActions}
                        </StyledDialogActions>
                    </>
                )}
            </StyledDialog>
        </ModalPortal>
    );
};
